import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendUp, TrendDown, Warning, CheckCircle } from '@phosphor-icons/react';
import Typhography from '../../../shared/Typhography/Typhography';
import { coachSessionsEndpoints } from '../../../endpoints/coachSessions';
import CustomTable from '../../../shared/CustomTable/CustomTable';
import styles from './TagBiasWidget.module.scss';

/**
 * Widget analisi bias tag tattici basato su GPS matched sessions.
 * Spec: LEOPART_ProgrammazioneCampo_Spec_v1.1.1.md righe 2144-2157.
 *
 * Mostra tag, sample size, delta medio IPS pianificato vs GPS reale,
 * suggerimento correzione peso. Actionable se |delta| > 0.5.
 */
export const TagBiasWidget: FC<{ teamId: string }> = ({ teamId }) => {
  const { data = [], isLoading } = useQuery({
    queryKey: ['tagBias', teamId],
    queryFn: () =>
      coachSessionsEndpoints.analyzeTagWeightBias(teamId, 60).then((r) => r.data),
    enabled: !!teamId,
    staleTime: 10 * 60_000,
  });

  if (isLoading) {
    return (
      <div className={styles.loading}>Analisi bias in corso…</div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={styles.empty}>
        Dati GPS insufficienti per analisi bias tag (servono almeno 3 sessioni
        matched-GPS per tag negli ultimi 60 giorni).
      </div>
    );
  }

  const actionables = data.filter((d) => d.actionable).length;

  return (
    <div className={styles.widget}>
      <div className={styles.headerRow}>
        <Typhography size="bold-14">
          Bias IPS per tag (ultimi 60gg)
        </Typhography>
        <span
          className={`${styles.badge} ${
            actionables > 0 ? styles.badgeWarn : styles.badgeOk
          }`}
        >
          {actionables > 0 ? (
            <>
              <Warning size={12} weight="fill" /> {actionables} tag da calibrare
            </>
          ) : (
            <>
              <CheckCircle size={12} weight="fill" /> Calibrazione OK
            </>
          )}
        </span>
      </div>

      <div className={styles.explainer}>
        Confronto IPS pianificato vs IPS equivalente da GPS. Delta
        positivo: piano sottostima il carico reale. Suggerito = delta × 0.2
        (smoothing 20%).
      </div>

      <CustomTable
        headers={['Tag', 'Sessioni', 'Δ medio', 'Aggiustamento', 'Status']}
        data={data}
        fields={{
          Tag: {
            key: 'tag',
            customComponent: (_: any, row) => <span className={styles.tagCell}>{row.tag}</span>,
          },
          Sessioni: { key: 'sampleSize' },
          'Δ medio': {
            key: 'deltaAvg',
            customComponent: (_: any, row) => (
              <span
                className={
                  row.deltaAvg > 0
                    ? styles.deltaUp
                    : row.deltaAvg < 0
                      ? styles.deltaDown
                      : ''
                }
              >
                {row.deltaAvg > 0 ? '+' : ''}
                {row.deltaAvg.toFixed(2)}
                {row.deltaAvg > 0 ? (
                  <TrendUp size={11} weight="bold" />
                ) : row.deltaAvg < 0 ? (
                  <TrendDown size={11} weight="bold" />
                ) : null}
              </span>
            ),
          },
          Aggiustamento: {
            key: 'suggestedAdjustment',
            customComponent: (_: any, row) => (
              <span className={styles.suggestion}>
                {row.suggestedAdjustment > 0 ? '+' : ''}
                {row.suggestedAdjustment.toFixed(2)}
              </span>
            ),
          },
          Status: {
            key: 'actionable',
            customComponent: (_: any, row) =>
              row.actionable ? (
                <span className={styles.actionable}>Calibrare</span>
              ) : (
                <span className={styles.stable}>Stabile</span>
              ),
          },
        }}
      />
    </div>
  );
};
